import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit
const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;
const TITLE_ALLOWED_REGEX = /^[a-zA-Z0-9\s\-_\(\)]*$/;
const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;
const SKU_ALLOWED_REGEX = /^[a-zA-Z0-9\-_]*$/;

const optionLabel = (item) => {
  if (item == null) {
    return "";
  }

  if (typeof item === "string") {
    return item.trim();
  }

  if (typeof item === "object") {
    return String(
      item.categoryName || item.collectionName || item.name || item.title || ""
    ).trim();
  }

  return String(item).trim();
};

const normalizeOptionItem = (item, index) => {
  if (item == null) {
    return null;
  }

  if (typeof item === "string") {
    const label = item.trim();
    if (!label) return null;
    return {
      iCategoryId: `str-${index}-${label}`,
      label,
      gender: "",
      mode: "",
      isActive: true,
    };
  }

  if (typeof item === "object") {
    const label = optionLabel(item);
    if (!label) {
      return null;
    }

    const activeFlag =
      item.bIsActive !== undefined
        ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
        : item.isActive !== undefined
          ? Boolean(item.isActive && item.isActive !== "0" && item.isActive !== 0)
          : true;

    return {
      iCategoryId: item.iCategoryId ?? item.id ?? item.categoryId ?? `obj-${index}`,
      label,
      gender: String(item.genderName || item.gender || "").trim().toLowerCase(),
      mode: String(item.modeName || item.mode || "").trim().toLowerCase(),
      isActive: activeFlag,
    };
  }

  const label = String(item).trim();
  return label
    ? {
        iCategoryId: `val-${index}-${label}`,
        label,
        gender: "",
        mode: "",
        isActive: true,
      }
    : null;
};

const sortCategories = (list) => {
  return [...list].sort((a, b) => {
    // Enabled categories (isActive === true) ALWAYS at the top
    if (Boolean(a.isActive) !== Boolean(b.isActive)) {
      return a.isActive ? -1 : 1;
    }
    // Alphabetical sort within same status
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" });
  });
};

const normalizeGenderValue = (genderInput, fallbackGender = "") => {
  if (!genderInput) {
    if (!fallbackGender) return "";
    return normalizeGenderValue(fallbackGender, "");
  }
  const str = String(genderInput).trim().toLowerCase();
  if (
    str === "women" ||
    str === "female" ||
    str === "w" ||
    str.includes("women") ||
    str.includes("female")
  ) {
    return "women";
  }
  if (
    str === "men" ||
    str === "male" ||
    str === "m" ||
    (str.includes("men") && !str.includes("women")) ||
    str.includes("male")
  ) {
    return "men";
  }
  if (fallbackGender && genderInput !== fallbackGender) {
    return normalizeGenderValue(fallbackGender, "");
  }
  return "";
};

const normalizeModeValue = (modeInput, fallbackMode = "") => {
  if (!modeInput) {
    if (!fallbackMode) return "";
    return normalizeModeValue(fallbackMode, "");
  }
  const str = String(modeInput).trim().toLowerCase();
  if (
    str === "mannequin" ||
    str === "mannequin_to_model" ||
    str.includes("mannequin")
  ) {
    return "mannequin_to_model";
  }
  if (
    str === "model" ||
    str === "model_to_model" ||
    str.includes("model")
  ) {
    return "model_to_model";
  }
  if (fallbackMode && modeInput !== fallbackMode) {
    return normalizeModeValue(fallbackMode, "");
  }
  return "";
};

const extractFilterCategoriesFromProduct = (product) => {
  if (!product) return [];
  const raw =
    product.filterCategories ??
    product.filterCategory ??
    product.categories ??
    product.sFilterCategory ??
    product.filter_categories ??
    product.sCategory ??
    product.category;

  if (Array.isArray(raw)) {
    return raw.map(String).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const extractCollectionCategoryFromProduct = (product) => {
  if (!product) return "";
  const raw =
    product.collectionCategory ??
    product.collectionName ??
    product.collection ??
    product.sCollectionCategory ??
    product.collection_category ??
    product.sCollection;

  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    return String(raw.name || raw.title || raw.categoryName || "").trim();
  }
  return "";
};

const matchesContext = (option, gender, mode) => {
  const targetGender = normalizeGenderValue(gender, "");
  const targetMode = normalizeModeValue(mode, "");

  const optionGender = normalizeGenderValue(option.gender, "");
  const optionMode = normalizeModeValue(option.mode, "");

  const genderMatch = !optionGender || optionGender === "all" || optionGender === targetGender;
  const modeMatch = !optionMode || optionMode === "all" || optionMode === targetMode;

  return genderMatch && modeMatch;
};

export const validateProductTitle = (title) => {
  const raw = String(title || "");
  const trimmed = raw.trim().replace(/\s+/g, " ");

  if (!trimmed) {
    return "Title is required";
  }
  if (trimmed.length < 3) {
    return "Title must be at least 3 characters";
  }
  if (trimmed.length > 100) {
    return "Title can be at most 100 characters";
  }
  if (!TITLE_ALLOWED_REGEX.test(raw)) {
    return "Title can only contain letters, numbers, spaces, -, _, and ()";
  }
  if (!CONTAINS_LETTER_REGEX.test(trimmed)) {
    return "Title must contain at least one letter";
  }
  return "";
};

export const validateProductSku = (sku, isEdit = false) => {
  const raw = String(sku || "");
  const trimmed = raw.trim();

  if (!trimmed) {
    return "SKU ID is required";
  }
  if (isEdit) {
    return "";
  }
  if (trimmed.length > 20) {
    return "SKU ID can be at most 20 characters";
  }
  if (!SKU_ALLOWED_REGEX.test(raw)) {
    return "SKU ID can only contain letters, numbers, -, and _";
  }
  return "";
};

export const validateProductPrice = (price) => {
  const raw = String(price !== undefined && price !== null ? price : "");
  const trimmed = raw.trim();

  if (!trimmed) {
    return "Price is required";
  }
  if (!PRICE_REGEX.test(trimmed)) {
    return "Price must be a positive number with up to 2 decimal places";
  }
  if (Number(trimmed) <= 0) {
    return "Price must be greater than 0";
  }
  if (Number(trimmed) > 9999999) {
    return "Price cannot exceed 99,99,999";
  }
  return "";
};

export const validateProductGender = (gender) => {
  if (!gender || !String(gender).trim()) {
    return "Gender is required";
  }
  const lower = String(gender).toLowerCase();
  if (!["women", "men"].includes(lower)) {
    return "Invalid gender selected";
  }
  return "";
};

export const validateProductMode = (mode) => {
  if (!mode || !String(mode).trim()) {
    return "Mode is required";
  }
  const lower = String(mode).toLowerCase();
  if (!["model_to_model", "mannequin_to_model"].includes(lower)) {
    return "Invalid mode selected";
  }
  return "";
};

export const validateProductFilterCategories = (cats) => {
  if (!Array.isArray(cats) || cats.length === 0) {
    return "Select at least one filter category";
  }
  return "";
};

export const validateProductCollectionCategory = (col) => {
  if (!col || !String(col).trim()) {
    return "Collection category is required";
  }
  return "";
};

export const useProductUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  getFilterCategories,
  getCollectionCategories,
  defaultGender = "",
  defaultMode = "",
  productToEdit = null,
}) => {
  const token = useSelector((state) => state.auth?.token);
  const isEditMode = Boolean(productToEdit);

  const initialFormState = {
    title: "",
    sku: "",
    price: "",
    description: "",
    gender: normalizeGenderValue(defaultGender, ""),
    mode: normalizeModeValue(defaultMode, ""),
    filterCategories: [],
    collectionCategory: "",
    frontImage: null,
    backImage: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [previewUrls, setPreviewUrls] = useState({
    front: null,
    back: null,
  });

  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [allFilterOptions, setAllFilterOptions] = useState([]);
  const [allCollectionOptions, setAllCollectionOptions] = useState([]);

  // Ref for auto-scrolling to errors on mobile
  const formRef = useRef(null);

  const resetForm = useCallback(() => {
    if (productToEdit) {
      const extractedGender = normalizeGenderValue(
        productToEdit.gender ||
        productToEdit.genderName ||
        productToEdit.gender_name ||
        productToEdit.sGender ||
        productToEdit.sGenderName ||
        productToEdit.eGender ||
        productToEdit.targetGender ||
        productToEdit.generationGender,
        defaultGender
      );
      const extractedMode = normalizeModeValue(
        productToEdit.mode ||
        productToEdit.modeName ||
        productToEdit.mode_name ||
        productToEdit.sMode ||
        productToEdit.sModeName ||
        productToEdit.eMode ||
        productToEdit.modelType ||
        productToEdit.sModelType ||
        productToEdit.generationType ||
        productToEdit.targetMode,
        defaultMode
      );
      const extractedFilters = extractFilterCategoriesFromProduct(productToEdit);
      const extractedCollection = extractCollectionCategoryFromProduct(productToEdit);

      setFormData({
        title: String(productToEdit.title ?? productToEdit.sTitle ?? productToEdit.name ?? ""),
        sku: String(
          productToEdit.sku ??
          productToEdit.vSku ??
          productToEdit.vSKU ??
          productToEdit.skuId ??
          productToEdit.iProductId ??
          productToEdit.id ??
          ""
        ),
        price: productToEdit.price !== undefined && productToEdit.price !== null ? String(productToEdit.price) : "",
        description: String(productToEdit.description ?? productToEdit.sDescription ?? ""),
        gender: extractedGender,
        mode: extractedMode,
        filterCategories: extractedFilters,
        collectionCategory: extractedCollection,
        frontImage: null,
        backImage: null,
      });
      setPreviewUrls({
        front: productToEdit.images?.[0] || productToEdit.frontImage || productToEdit.front || productToEdit.image || null,
        back: productToEdit.images?.[1] || productToEdit.backImage || productToEdit.back || null,
      });
    } else {
      setFormData({
        title: "",
        sku: "",
        price: "",
        description: "",
        gender: normalizeGenderValue(defaultGender, ""),
        mode: normalizeModeValue(defaultMode, ""),
        filterCategories: [],
        collectionCategory: "",
        frontImage: null,
        backImage: null,
      });
      setPreviewUrls({ front: null, back: null });
    }
    if (typeof document !== "undefined") {
      const frontInput = document.getElementById("frontImage");
      if (frontInput) frontInput.value = "";
      const backInput = document.getElementById("backImage");
      if (backInput) backInput.value = "";
    }
    setErrors({});
    setOptionsError("");
  }, [productToEdit, defaultGender, defaultMode]);

  const loadCategories = useCallback(
    async (genderVal, modeVal) => {
      if (!getFilterCategories || !getCollectionCategories) {
        return;
      }

      setOptionsLoading(true);
      setOptionsError("");

      try {
        const params = {};
        if (genderVal) params.gender = genderVal;
        if (modeVal) params.mode = modeVal;

        const [filtersResult, collectionsResult] = await Promise.all([
          getFilterCategories(params),
          getCollectionCategories(params),
        ]);

        if (!filtersResult.success) {
          throw new Error(filtersResult.error || "Unable to load filter categories");
        }

        if (!collectionsResult.success) {
          throw new Error(collectionsResult.error || "Unable to load collection categories");
        }

        const normalizedFilterOptions = (filtersResult.data || [])
          .map(normalizeOptionItem)
          .filter(Boolean);
        const normalizedCollectionOptions = (collectionsResult.data || [])
          .map(normalizeOptionItem)
          .filter(Boolean);

        setAllFilterOptions(normalizedFilterOptions);
        setAllCollectionOptions(normalizedCollectionOptions);
      } catch (err) {
        setAllFilterOptions([]);
        setAllCollectionOptions([]);
        setOptionsError(err.message || "Failed to load category options");
      } finally {
        setOptionsLoading(false);
      }
    },
    [getFilterCategories, getCollectionCategories]
  );

  const prevProductRef = useRef(productToEdit);
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      if (prevIsOpenRef.current) {
        resetForm();
      }
      prevIsOpenRef.current = false;
      return;
    }

    const justOpened = !prevIsOpenRef.current && isOpen;
    prevIsOpenRef.current = isOpen;

    const prevProduct = prevProductRef.current;
    const prevId = prevProduct?.iProductId ?? prevProduct?.id ?? prevProduct?.skuId ?? prevProduct?.sku ?? null;
    const currId = productToEdit?.iProductId ?? productToEdit?.id ?? productToEdit?.skuId ?? productToEdit?.sku ?? null;

    const isProductChanged = prevId !== currId || Boolean(prevProduct) !== Boolean(productToEdit);

    if (justOpened || isProductChanged) {
      prevProductRef.current = productToEdit;
      resetForm();

      const targetGender = productToEdit
        ? normalizeGenderValue(
            productToEdit.gender ||
            productToEdit.genderName ||
            productToEdit.gender_name ||
            productToEdit.sGender ||
            productToEdit.sGenderName ||
            productToEdit.eGender ||
            productToEdit.targetGender ||
            productToEdit.generationGender,
            defaultGender
          )
        : normalizeGenderValue(defaultGender, "");

      const targetMode = productToEdit
        ? normalizeModeValue(
            productToEdit.mode ||
            productToEdit.modeName ||
            productToEdit.mode_name ||
            productToEdit.sMode ||
            productToEdit.sModeName ||
            productToEdit.eMode ||
            productToEdit.modelType ||
            productToEdit.sModelType ||
            productToEdit.generationType ||
            productToEdit.targetMode,
            defaultMode
          )
        : normalizeModeValue(defaultMode, "");

      loadCategories(targetGender, targetMode);
    }
  }, [isOpen, productToEdit, defaultGender, defaultMode, loadCategories, resetForm]);

  const filterOptions = useMemo(() => {
    const visible = allFilterOptions.filter((item) =>
      matchesContext(item, formData.gender, formData.mode)
    );
    const existingLabels = new Set(visible.map((item) => item.label));
    const extraSelected = (formData.filterCategories || [])
      .filter((lbl) => !existingLabels.has(lbl))
      .map((lbl, idx) => ({
        iCategoryId: `extra-filter-${idx}-${lbl}`,
        label: lbl,
        gender: formData.gender,
        mode: formData.mode,
        isActive: true,
      }));

    return sortCategories([...visible, ...extraSelected]);
  }, [allFilterOptions, formData.gender, formData.mode, formData.filterCategories]);

  const collectionOptions = useMemo(() => {
    const visible = allCollectionOptions.filter((item) =>
      matchesContext(item, formData.gender, formData.mode)
    );
    const existingLabels = new Set(visible.map((item) => item.label));
    const extraSelected =
      formData.collectionCategory && !existingLabels.has(formData.collectionCategory)
        ? [
            {
              iCategoryId: `extra-col-${formData.collectionCategory}`,
              label: formData.collectionCategory,
              gender: formData.gender,
              mode: formData.mode,
              isActive: true,
            },
          ]
        : [];

    return sortCategories([...visible, ...extraSelected]);
  }, [allCollectionOptions, formData.gender, formData.mode, formData.collectionCategory]);

  const handleToggleCategory = async (categoryId, currentIsActive) => {
    if (!categoryId || String(categoryId).startsWith("extra-") || String(categoryId).startsWith("str-")) {
      toast.error("Category ID not available for toggling.");
      return;
    }

    const newStatus = !currentIsActive;

    const updateStatusInList = (list) =>
      list.map((item) =>
        String(item.iCategoryId) === String(categoryId)
          ? { ...item, isActive: newStatus }
          : item
      );

    setAllFilterOptions((prev) => updateStatusInList(prev));
    setAllCollectionOptions((prev) => updateStatusInList(prev));

    try {
      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.TOGGLE_CATEGORY}/${categoryId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(response?.data?.message || "Failed to toggle category status");
      }

      const resData = response?.data?.data;
      const finalStatus =
        resData?.bIsActive !== undefined
          ? Boolean(resData.bIsActive && resData.bIsActive !== "0" && resData.bIsActive !== 0)
          : resData?.isActive !== undefined
            ? Boolean(resData.isActive && resData.isActive !== "0" && resData.isActive !== 0)
            : newStatus;

      const setFinalStatus = (list) =>
        list.map((item) =>
          String(item.iCategoryId) === String(categoryId)
            ? { ...item, isActive: finalStatus }
            : item
        );

      setAllFilterOptions((prev) => setFinalStatus(prev));
      setAllCollectionOptions((prev) => setFinalStatus(prev));

      toast.success(
        response?.data?.message ||
          `Category ${finalStatus ? "enabled" : "disabled"} successfully`
      );
    } catch (err) {
      const revertStatus = (list) =>
        list.map((item) =>
          String(item.iCategoryId) === String(categoryId)
            ? { ...item, isActive: currentIsActive }
            : item
        );

      setAllFilterOptions((prev) => revertStatus(prev));
      setAllCollectionOptions((prev) => revertStatus(prev));

      toast.error(err?.message || "Failed to toggle category status");
    }
  };

  useEffect(() => {
    if (optionsLoading) {
      return;
    }

    if (allFilterOptions.length === 0 && allCollectionOptions.length === 0) {
      return;
    }

    const filterLabels = filterOptions.map((o) => o.label);
    const collectionLabels = collectionOptions.map((o) => o.label);

    setFormData((prev) => {
      const nextFilterCategories = prev.filterCategories.filter((category) =>
        filterLabels.includes(category)
      );
      const nextCollectionCategory = collectionLabels.includes(prev.collectionCategory)
        ? prev.collectionCategory
        : "";

      if (
        nextFilterCategories.length === prev.filterCategories.length &&
        nextCollectionCategory === prev.collectionCategory
      ) {
        return prev;
      }

      return {
        ...prev,
        filterCategories: nextFilterCategories,
        collectionCategory: nextCollectionCategory,
      };
    });
  }, [filterOptions, collectionOptions, optionsLoading, allFilterOptions.length, allCollectionOptions.length]);

  const validateImageFile = (file, existingPreviewUrl, imageType = "front") => {
    if (!file) {
      if (existingPreviewUrl) return "";
      return "Image is required";
    }

    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
      return "Only jpg, jpeg and png formats are allowed";
    }

    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 10MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`;
    }

    const lowerName = file.name.toLowerCase();

    if (imageType === "front") {
      if (!lowerName.includes("front")) {
        if (lowerName.includes("back")) {
          return "Filename contains 'back'. Please upload it to the Back View field";
        }
        return "Front image file name must contain 'front' (e.g. product_front.jpg)";
      }
      if (lowerName.includes("back")) {
        return "Front image file name cannot contain 'back'";
      }
    } else if (imageType === "back") {
      if (!lowerName.includes("back")) {
        if (lowerName.includes("front")) {
          return "Filename contains 'front'. Please upload it to the Front View field";
        }
        return "Back image file name must contain 'back' (e.g. product_back.jpg)";
      }
      if (lowerName.includes("front")) {
        return "Back image file name cannot contain 'front'";
      }
    }

    return "";
  };

  const validateForm = () => {
    const nextErrors = {};

    const trimmedPrice = String(formData.price || "").trim();
    const trimmedDescription = String(formData.description || "").trim();

    const titleError = validateProductTitle(formData.title);
    if (titleError) {
      nextErrors.title = titleError;
    }

    const skuError = validateProductSku(formData.sku, isEditMode);
    if (skuError && !isEditMode) {
      nextErrors.sku = skuError;
    }

    if (!trimmedPrice) {
      nextErrors.price = "Price is required";
    } else if (!PRICE_REGEX.test(trimmedPrice)) {
      nextErrors.price = "Price must be a positive number with up to 2 decimal places";
    } else if (Number(trimmedPrice) <= 0) {
      nextErrors.price = "Price must be greater than 0";
    } else if (Number(trimmedPrice) > 9999999) {
      nextErrors.price = "Price cannot exceed 99,99,999";
    }

    if (trimmedDescription.length > 2000) {
      nextErrors.description = "Description can be at most 2000 characters";
    }

    if (!formData.gender) {
      nextErrors.gender = "Gender is required";
    }

    if (!formData.mode) {
      nextErrors.mode = "Mode is required";
    }

    if (!Array.isArray(formData.filterCategories) || formData.filterCategories.length === 0) {
      nextErrors.filterCategories = "Select at least one filter category";
    }

    if (!formData.collectionCategory) {
      nextErrors.collectionCategory = "Collection category is required";
    }

    const frontImageError = validateImageFile(formData.frontImage, previewUrls.front, "front");
    if (frontImageError) {
      nextErrors.frontImage = frontImageError;
    }

    const backImageError = validateImageFile(formData.backImage, previewUrls.back, "back");
    if (backImageError) {
      nextErrors.backImage = backImageError;
    }

    if (
      !frontImageError &&
      !backImageError &&
      formData.frontImage &&
      formData.backImage &&
      formData.frontImage.name === formData.backImage.name &&
      formData.frontImage.size === formData.backImage.size
    ) {
      nextErrors.backImage = "Front and Back images cannot be the same file";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setTimeout(() => {
        const firstErrorKey = Object.keys(nextErrors)[0];
        const errorElement = formRef.current?.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}, #${firstErrorKey}Card, [data-field="${firstErrorKey}"]`
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (typeof errorElement.focus === "function" && errorElement.tabIndex !== -1) {
            errorElement.focus({ preventScroll: true });
          }
        }
      }, 100);
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "sku" && isEditMode) {
      return;
    }

    if (name === "gender" || name === "mode") {
      const nextGender = name === "gender" ? value : formData.gender;
      const nextMode = name === "mode" ? value : formData.mode;

      // Reset filter and collection categories when gender or mode changes
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        filterCategories: [],
        collectionCategory: "",
      }));

      // Clear validation errors for these fields
      setErrors((prev) => ({
        ...prev,
        [name]: "",
        filterCategories: "",
        collectionCategory: "",
      }));

      // Re-fetch category lists for the updated gender and mode combination
      loadCategories(nextGender, nextMode);
      return;
    }

    if (name === "title") {
      const sanitized = value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
      const titleError = validateProductTitle(sanitized);
      setFormData((prev) => ({
        ...prev,
        title: sanitized,
      }));
      setErrors((prev) => ({
        ...prev,
        title: titleError,
      }));
      return;
    }

    if (name === "sku") {
      const skuError = validateProductSku(value);
      setFormData((prev) => ({
        ...prev,
        sku: value,
      }));
      setErrors((prev) => ({
        ...prev,
        sku: skuError,
      }));
      return;
    }

    if (name === "price") {
      const priceError = validateProductPrice(value);
      setFormData((prev) => ({
        ...prev,
        price: value,
      }));
      setErrors((prev) => ({
        ...prev,
        price: priceError,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    if (name === "title") {
      const titleError = validateProductTitle(value);
      setErrors((prev) => ({
        ...prev,
        title: titleError,
      }));
    } else if (name === "sku") {
      const skuError = validateProductSku(value);
      setErrors((prev) => ({
        ...prev,
        sku: skuError,
      }));
    } else if (name === "price") {
      const priceError = validateProductPrice(value);
      setErrors((prev) => ({
        ...prev,
        price: priceError,
      }));
    } else if (name === "gender") {
      const genderError = validateProductGender(value);
      setErrors((prev) => ({
        ...prev,
        gender: genderError,
      }));
    } else if (name === "mode") {
      const modeError = validateProductMode(value);
      setErrors((prev) => ({
        ...prev,
        mode: modeError,
      }));
    }
  };

  const isFormValid = useMemo(() => {
    if (validateProductTitle(formData.title)) return false;
    if (validateProductSku(formData.sku, isEditMode)) return false;
    if (validateProductPrice(formData.price)) return false;
    if (String(formData.description || "").trim().length > 2000) return false;

    if (validateProductGender(formData.gender)) return false;
    if (validateProductMode(formData.mode)) return false;

    if (validateProductFilterCategories(formData.filterCategories)) return false;
    if (validateProductCollectionCategory(formData.collectionCategory)) return false;

    if (validateImageFile(formData.frontImage, previewUrls.front, "front")) return false;
    if (validateImageFile(formData.backImage, previewUrls.back, "back")) return false;
    if (
      formData.frontImage &&
      formData.backImage &&
      formData.frontImage.name === formData.backImage.name &&
      formData.frontImage.size === formData.backImage.size
    ) {
      return false;
    }

    if (
      errors.title ||
      (!isEditMode && errors.sku) ||
      errors.price ||
      errors.description ||
      errors.gender ||
      errors.mode ||
      errors.filterCategories ||
      errors.collectionCategory ||
      errors.frontImage ||
      errors.backImage
    ) {
      return false;
    }

    return true;
  }, [formData, previewUrls, errors, isEditMode]);

  const handleImageChange = (e, imageType) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const fieldName = imageType === "front" ? "frontImage" : "backImage";
    const otherFieldName = imageType === "front" ? "backImage" : "frontImage";
    const otherFile = formData[otherFieldName];

    const imageError = validateImageFile(file, null, imageType);

    if (imageError) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: imageError,
      }));
      e.target.value = "";
      return;
    }

    if (otherFile && otherFile.name === file.name && otherFile.size === file.size) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "Front and Back images cannot be the same file",
      }));
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrls((prev) => ({
        ...prev,
        [imageType]: reader.result,
      }));
    };
    reader.readAsDataURL(file);

    setFormData((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    setErrors((prev) => {
      const updated = {
        ...prev,
        [fieldName]: "",
      };
      if (updated[otherFieldName] === "Front and Back images cannot be the same file") {
        updated[otherFieldName] = "";
      }
      return updated;
    });
  };

  const handleRemoveImage = (imageType) => {
    const fieldName = imageType === "front" ? "frontImage" : "backImage";
    const otherFieldName = imageType === "front" ? "backImage" : "frontImage";

    setFormData((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
    setPreviewUrls((prev) => ({
      ...prev,
      [imageType]: null,
    }));

    const inputElement = document.getElementById(fieldName);
    if (inputElement) {
      inputElement.value = "";
    }

    setErrors((prev) => {
      const updated = {
        ...prev,
        [fieldName]: "",
      };
      if (updated[otherFieldName] === "Front and Back images cannot be the same file") {
        updated[otherFieldName] = "";
      }
      return updated;
    });
  };

  const hasChanges = useMemo(() => {
    if (!productToEdit) return true;

    const initialGender = normalizeGenderValue(
      productToEdit.gender || productToEdit.genderName || productToEdit.sGender || productToEdit.category,
      defaultGender
    );
    const initialMode = normalizeModeValue(
      productToEdit.mode || productToEdit.modeName || productToEdit.sMode || productToEdit.modelType,
      defaultMode
    );
    const initialFilters = extractFilterCategoriesFromProduct(productToEdit);
    const initialCollection = extractCollectionCategoryFromProduct(productToEdit);
    const initialTitle = String(productToEdit.title ?? productToEdit.sTitle ?? productToEdit.name ?? "").trim();
    const initialSku = String(
      productToEdit.sku ??
      productToEdit.vSku ??
      productToEdit.vSKU ??
      productToEdit.skuId ??
      productToEdit.iProductId ??
      productToEdit.id ??
      ""
    ).trim();
    const initialPrice = productToEdit.price !== undefined && productToEdit.price !== null ? String(productToEdit.price).trim() : "";
    const initialDescription = String(productToEdit.description ?? productToEdit.sDescription ?? "").trim();

    if (String(formData.title || "").trim() !== initialTitle) return true;
    if (String(formData.sku || "").trim() !== initialSku) return true;
    if (String(formData.price || "").trim() !== initialPrice) return true;
    if (String(formData.description || "").trim() !== initialDescription) return true;
    if (formData.gender !== initialGender) return true;
    if (formData.mode !== initialMode) return true;
    if (String(formData.collectionCategory || "").trim() !== initialCollection) return true;

    const currentFilters = (formData.filterCategories || []).map((s) => String(s).trim()).sort();
    const origFilters = (initialFilters || []).map((s) => String(s).trim()).sort();
    if (currentFilters.length !== origFilters.length) return true;
    if (currentFilters.some((val, i) => val !== origFilters[i])) return true;

    if (formData.frontImage !== null) return true;
    if (formData.backImage !== null) return true;

    return false;
  }, [formData, productToEdit, defaultGender, defaultMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all mandatory fields correctly.", {
        id: "mandatory-fields-error",
      });
      return;
    }

    if (productToEdit) {
      if (!hasChanges) {
        toast.info("No changes were made to update.");
        return;
      }

      // Edit mode: Send FormData payload matching upload specification, including new images if provided
      const payload = new FormData();
      const cleanTitle = String(formData.title || "").trim().replace(/\s+/g, " ");
      payload.append("title", cleanTitle);
      const cleanSku = String(formData.sku || "").trim();
      if (cleanSku) {
        payload.append("sku", cleanSku);
        payload.append("vSku", cleanSku);
      }
      payload.append("price", String(formData.price || "").trim());
      payload.append("description", String(formData.description || "").trim());
      payload.append("gender", formData.gender);
      payload.append("mode", formData.mode);
      payload.append("filterCategories", formData.filterCategories.join(", "));
      payload.append("collectionCategory", formData.collectionCategory);
      if (formData.frontImage) {
        payload.append("frontImage", formData.frontImage);
      }
      if (formData.backImage) {
        payload.append("backImage", formData.backImage);
      }

      if (onSubmit) {
        const result = await onSubmit(payload, productToEdit);
        if (!result || result.success !== false) {
          resetForm();
        }
      }
    } else {
      // Add mode: Send FormData payload matching POST upload specification
      const payload = new FormData();
      const cleanTitle = String(formData.title || "").trim().replace(/\s+/g, " ");
      payload.append("title", cleanTitle);
      const cleanSku = String(formData.sku || "").trim();
      if (cleanSku) {
        payload.append("sku", cleanSku);
        payload.append("vSku", cleanSku);
      }
      payload.append("price", String(formData.price || "").trim());
      payload.append("description", String(formData.description || "").trim());
      payload.append("gender", formData.gender);
      payload.append("mode", formData.mode);
      payload.append("filterCategories", formData.filterCategories.join(", "));
      payload.append("collectionCategory", formData.collectionCategory);
      payload.append("frontImage", formData.frontImage);
      payload.append("backImage", formData.backImage);

      if (onSubmit) {
        const result = await onSubmit(payload, null);
        if (!result || result.success !== false) {
          resetForm();
        }
      }
    }
  };

  const isFormDirty = useMemo(() => {
    if (isEditMode) {
      return hasChanges;
    }
    return Boolean(
      (formData.title && String(formData.title).trim()) ||
        (formData.sku && String(formData.sku).trim()) ||
        (formData.price && String(formData.price).trim()) ||
        (formData.description && String(formData.description).trim()) ||
        formData.gender !== normalizeGenderValue(defaultGender, "") ||
        formData.mode !== normalizeModeValue(defaultMode, "") ||
        (formData.filterCategories && formData.filterCategories.length > 0) ||
        formData.collectionCategory ||
        formData.frontImage ||
        formData.backImage ||
        Object.keys(errors).length > 0
    );
  }, [isEditMode, hasChanges, formData, defaultGender, defaultMode, errors]);

  const handleReset = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleCancel = useCallback(() => {
    resetForm();
    if (onClose) {
      onClose();
    }
  }, [resetForm, onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e && e.target !== e.currentTarget) return;
      if (onClose) {
        onClose();
      }
    },
    [onClose]
  );

  const handleClose = handleCancel;

  const isDetailsFilled =
    !!String(formData.title || "").trim() &&
    !!String(formData.sku || "").trim() &&
    !!String(formData.price || "").trim() &&
    !errors.title &&
    !errors.sku &&
    !errors.price;
  const isConfigFilled = !!formData.gender && !!formData.mode && formData.filterCategories.length > 0 && !!formData.collectionCategory;
  const isImagesFilled = (!!formData.frontImage || !!previewUrls.front) && (!!formData.backImage || !!previewUrls.back);
  const isFormComplete = isDetailsFilled && isConfigFilled && isImagesFilled;

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    previewUrls,
    optionsLoading,
    optionsError,
    filterOptions,
    collectionOptions,
    formRef,
    handleInputChange,
    handleInputBlur,
    handleImageChange,
    handleRemoveImage,
    handleSubmit,
    handleClose,
    handleReset,
    handleCancel,
    handleBackdropClick,
    handleToggleCategory,
    isDetailsFilled,
    isConfigFilled,
    isImagesFilled,
    isFormComplete,
    isFormValid,
    isEditMode,
    hasChanges,
    isFormDirty,
  };
};
